import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, ToggleLeft, ToggleRight, Loader2, Database } from 'lucide-react';
import { Strategy, StrategyCreate, StrategyUpdate } from '../types/strategy';
import * as strategySvc from '../services/strategyService';
import { formatPercent } from '../utils';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../components/Toast';

const INDICATORS = ['RSI', 'MACD', 'Bollinger Bands', 'SMA', 'EMA', 'Volume', 'ATR', 'Momentum', 'Other'];
const SYMBOLS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'ADA/USDT', 'XRP/USDT', 'BNB/USDT', 'DOGE/USDT', 'Other'];

const emptyForm = (): StrategyCreate => ({
  name: '',
  symbol: 'BTC/USDT',
  indicator: 'RSI',
  trigger_condition: '',
  action: 'BUY',
  position_size_pct: 0.1,
  stop_loss_pct: 0.02,
  take_profit_pct: 0.05,
  entry_signal: '',
  exit_signal: '',
  description: '',
  parameters: {},
});

const StrategiesPage: React.FC = () => {
  const toast = useToast();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null);
  const [form, setForm] = useState<StrategyCreate>(emptyForm());
  const [paramsRaw, setParamsRaw] = useState('{}');
  const [paramsError, setParamsError] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<Strategy | null>(null);

  // ---------------------------------------------------------------------------

  const loadStrategies = async () => {
    try {
      const data = await strategySvc.getStrategies();
      setStrategies(data);
    } catch (err) {
      console.error('Failed to load strategies', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStrategies(); }, []);

  // ---------------------------------------------------------------------------
  // Create / Edit
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    setEditingStrategy(null);
    setForm(emptyForm());
    setParamsRaw('{}');
    setParamsError('');
    setShowModal(true);
  };

  const openEdit = (s: Strategy) => {
    setEditingStrategy(s);
    setForm({
      name: s.name,
      symbol: s.symbol,
      indicator: s.indicator,
      trigger_condition: s.trigger_condition,
      action: s.action,
      position_size_pct: s.position_size_pct,
      stop_loss_pct: s.stop_loss_pct,
      take_profit_pct: s.take_profit_pct,
      entry_signal: s.entry_signal ?? '',
      exit_signal: s.exit_signal ?? '',
      description: s.description ?? '',
      parameters: s.parameters ?? {},
    });
    setParamsRaw(JSON.stringify(s.parameters ?? {}, null, 2));
    setParamsError('');
    setShowModal(true);
  };

  const saveStrategy = async () => {
    if (!form.name.trim()) return;
    let params: Record<string, unknown> = {};
    try {
      params = JSON.parse(paramsRaw || '{}');
    } catch {
      setParamsError('Invalid JSON in parameters');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, parameters: params };
      if (editingStrategy) {
        const updates: StrategyUpdate = payload;
        await strategySvc.updateStrategy(editingStrategy.id, updates);
      } else {
        await strategySvc.createStrategy(payload);
      }
      setShowModal(false);
      await loadStrategies();
    } catch (err) {
      console.error('Save strategy failed', err);
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Toggle / Delete / Seed
  // ---------------------------------------------------------------------------

  const handleToggle = async (s: Strategy) => {
    try {
      await strategySvc.toggleStrategy(s.id, !s.enabled);
      await loadStrategies();
    } catch (err) {
      console.error('Toggle failed', err);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await strategySvc.deleteStrategy(deleteTarget.id);
      setDeleteTarget(null);
      await loadStrategies();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const seedFromCsv = async () => {
    setSeeding(true);
    try {
      const res = await strategySvc.seedStrategiesFromCsv();
      await loadStrategies();
      toast('success', `Seeded ${res.inserted} strategies from CSV.`);
    } catch (err) {
      console.error('Seed failed', err);
      toast('error', 'Seed failed — check that the CSV file exists on the backend.');
    } finally {
      setSeeding(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Trading Strategies</h1>
          <p className="text-slate-400">Define and manage strategies that agents use to generate trading signals</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={seedFromCsv}
            disabled={seeding}
            title="Seed strategies from CSV file"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm transition disabled:opacity-50"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            Seed from CSV
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm transition"
          >
            <Plus className="w-4 h-4" />
            New Strategy
          </button>
        </div>
      </div>

      {/* Strategy cards */}
      {loading ? (
        <div className="text-center text-slate-400 py-16">Loading strategies...</div>
      ) : strategies.length === 0 ? (
        <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700 p-12 text-center">
          <p className="text-slate-400 mb-2">No strategies yet.</p>
          <p className="text-slate-500 text-sm">
            Click <span className="text-blue-400">New Strategy</span> to create one, or{' '}
            <button onClick={seedFromCsv} className="text-blue-400 hover:underline">Seed from CSV</button> to import the defaults.
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  {['Strategy','Symbol','Indicator','Condition','Action','Pos Size','Stop Loss','Take Profit','Status',''].map((h, i) => (
                    <th key={i} className="px-5 py-3 text-left text-xs font-semibold text-slate-300 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {strategies.map(s => (
                  <tr key={s.id} className="hover:bg-slate-700/30 transition">
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-white">{s.name}</div>
                      {s.description && <div className="text-xs text-slate-500 mt-0.5 max-w-xs truncate">{s.description}</div>}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300 font-mono">{s.symbol}</td>
                    <td className="px-5 py-4 text-sm text-slate-300">{s.indicator}</td>
                    <td className="px-5 py-4 text-xs text-slate-400 font-mono max-w-xs truncate">{s.trigger_condition}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${s.action === 'BUY' ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'}`}>
                        {s.action}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300">{formatPercent(s.position_size_pct)}</td>
                    <td className="px-5 py-4 text-sm text-slate-300">{formatPercent(s.stop_loss_pct)}</td>
                    <td className="px-5 py-4 text-sm text-slate-300">{formatPercent(s.take_profit_pct)}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => handleToggle(s)} className="flex items-center gap-1.5 text-xs transition">
                        {s.enabled
                          ? <><ToggleRight className="w-4 h-4 text-green-400" /><span className="text-green-300">Enabled</span></>
                          : <><ToggleLeft className="w-4 h-4 text-slate-500" /><span className="text-slate-500">Disabled</span></>
                        }
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition" title="Edit">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteTarget(s)} className="p-1.5 rounded-md hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Create / Edit Modal                                                  */}
      {/* ------------------------------------------------------------------ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">{editingStrategy ? 'Edit Strategy' : 'New Strategy'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Name + Action row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-1">Strategy Name <span className="text-red-400">*</span></label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. RSI Oversold Buy" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Action</label>
                  <div className="flex rounded-lg overflow-hidden border border-slate-600">
                    {['BUY', 'SELL'].map(a => (
                      <button key={a} onClick={() => setForm(f => ({ ...f, action: a }))} className={`flex-1 py-2 text-sm font-medium transition ${form.action === a ? (a === 'BUY' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300') : 'text-slate-400 hover:text-white'}`}>{a}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Symbol + Indicator row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Symbol</label>
                  <select value={form.symbol} onChange={e => setForm(f => ({ ...f, symbol: e.target.value }))} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
                    {SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Indicator</label>
                  <select value={form.indicator} onChange={e => setForm(f => ({ ...f, indicator: e.target.value }))} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
                    {INDICATORS.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>

              {/* Trigger condition */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Trigger Condition <span className="text-red-400">*</span></label>
                <input value={form.trigger_condition} onChange={e => setForm(f => ({ ...f, trigger_condition: e.target.value }))} placeholder="e.g. RSI < 30" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
              </div>

              {/* Position sizing row */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Position Size %', key: 'position_size_pct' as const, placeholder: '0.1' },
                  { label: 'Stop Loss %', key: 'stop_loss_pct' as const, placeholder: '0.02' },
                  { label: 'Take Profit %', key: 'take_profit_pct' as const, placeholder: '0.05' },
                ].map(({ label, key, placeholder }) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: parseFloat(e.target.value) || 0 }))}
                      placeholder={placeholder}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                ))}
              </div>

              {/* Entry / Exit signals */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Entry Signal <span className="text-slate-500 font-normal">(optional)</span></label>
                  <input value={form.entry_signal ?? ''} onChange={e => setForm(f => ({ ...f, entry_signal: e.target.value }))} placeholder="e.g. RSI crosses below 30" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Exit Signal <span className="text-slate-500 font-normal">(optional)</span></label>
                  <input value={form.exit_signal ?? ''} onChange={e => setForm(f => ({ ...f, exit_signal: e.target.value }))} placeholder="e.g. RSI crosses above 50" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Description <span className="text-slate-500 font-normal">(optional)</span></label>
                <textarea value={form.description ?? ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Brief description of this strategy's purpose..." className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 resize-none" />
              </div>

              {/* Parameters JSON */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Parameters JSON <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={paramsRaw}
                  onChange={e => { setParamsRaw(e.target.value); setParamsError(''); }}
                  rows={3}
                  placeholder='{"period": 14, "overbought": 70, "oversold": 30}'
                  className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500 resize-none ${paramsError ? 'border-red-500' : 'border-slate-600'}`}
                />
                {paramsError && <p className="text-xs text-red-400 mt-1">{paramsError}</p>}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm">Cancel</button>
              <button
                onClick={saveStrategy}
                disabled={saving || !form.name.trim() || !form.trigger_condition.trim()}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingStrategy ? 'Save Changes' : 'Create Strategy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Strategy?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default StrategiesPage;
